/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/icon.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { PropertyViz } from 'components/PropertyViz'
import { DataElementKey } from 'components/DataElementKey'
import { DataElementTooltip } from 'components/DataElementTooltip'
import { extent } from 'd3-array'
import { scaleSqrt, scaleQuantile } from 'd3-scale'
import { DataElement } from 'components/DataElement'
import { csvParse } from 'd3-dsv'
import { TEXT_FR } from '../_text_fr'
import { TEXT_DE } from '../_text_de'
import {
  COLORS,
  PROP_ORDER,
  CHAPTER_DE,
  CHAPTER_FR,
  PROPS_NO_DATA
} from './config'
//

/**
 
 To deploy German or French Version::
 - Change LOCALE below
 - copy content of .gitlab-ci-de.yml  or .gitlab-ci-fr.yml into gitlab-ci.yml
 - copy content of .taStandalonerc-de.yml  or .taStandalonerc-fr.yml into .taStandalonerc.yml
 - push to to production-branch

 */

//
const LOCALE: 'DE' | 'FR' = 'DE'
const SHEET_URL =
  'https://docs.google.com/spreadsheets/d/e/2PACX-1vSCdtNmgfb9gOdIxC7GkqjaDyiq4Nib3_wC8_Inu1LbskidW5_uAwB7pHMkb2feTh1I1Zcl-r9hoCCS/pub?gid=443877501&single=true&output=csv'
//
const ARTICLE_ID_DE = '253612265827'
const ARTICLE_ID_FR = '847522530745'
const ARTICLE_ID = LOCALE === 'DE' ? ARTICLE_ID_DE : ARTICLE_ID_FR
//
//
const TEXT = LOCALE === 'DE' ? TEXT_DE : TEXT_FR
//
const CHAPTER = LOCALE === 'DE' ? CHAPTER_DE : CHAPTER_FR
//
const radScale = scaleSqrt<number, number>()
  .domain([0, 100])
  .range([24, 5, 24])

const colorScale = scaleQuantile<string, string>().range(COLORS)
const colorScaleRev = scaleQuantile<string, string>().range(
  [...COLORS].reverse()
)

const colorByValue: (
  val: number,
  dir: 'normal' | 'reverse',
  all: number[]
) => string = (val, dir, all) => {
  colorScaleRev.domain(all)
  colorScale.domain(all)
  return dir === 'normal' ? colorScaleRev(val) : colorScale(val)
}

export function AppComponent() {
  const [drawdata, setdrawdata] = useState<
    { [key: string]: string | number }[]
  >([])
  const [switzerland, setswitzerland] = useState<
    { [key: string]: string | number }[] | undefined
  >()

  const [drawaverages, setdrawaverages] = useState<{ [key: string]: string }>(
    {}
  )
  const [drawprops, setdrawprops] = useState<
    {
      name: string
      unit: string
      prop: string
      dir: 'normal' | 'reverse'
      title: string
      anno: [string, number][]
    }[]
  >()
  const [hoveredElement, sethoveredElement] = useState<
    | {
        data: {
          name: string
          props: { unit: string; prop: string; val: string; color: string }[]
        }
        pos: [number, number]
      }
    | undefined
  >()
  useEffect(() => {
    fetch(SHEET_URL)
      .then(d => d.text())
      .then(d => {
        console.log('FETCH', csvParse(d))

        const sheetdata = csvParse(d)

        const directions = sheetdata.shift()
        const names = sheetdata.shift()
        const units = sheetdata.shift()
        const names_fr = sheetdata.shift()
        const units_fr = sheetdata.shift()
        const positions = sheetdata.shift()
        const props = Object.keys(directions)
          .filter(f => PROPS_NO_DATA.indexOf(f) === -1 && !f.includes('_abs'))
          .map(m => ({
            prop: m,
            dir: directions[m],
            name: LOCALE === 'DE' ? names[m] : names_fr[m],
            unit: LOCALE === 'DE' ? units[m] : units_fr[m],
            title: CHAPTER[m].title,
            anno: CHAPTER[m].anno,
            pos: +positions[m]
          }))
          .sort((a, b) => {
            return PROP_ORDER.indexOf(a.prop) - PROP_ORDER.indexOf(b.prop)
          })

        const averages = sheetdata.shift()

        // console.log('Data', sheetdata)
        // console.log('Props', props)
        // console.log('Averages', averages)
        setswitzerland(sheetdata.find(f => f.code === 'CH'))
        setdrawprops(props)
        setdrawdata(sheetdata.sort((a, b) => a.rank - b.rank))
        setdrawaverages(averages)
      })
  }, [])
  return (
    <div className='index' style={{ position: 'relative' }}>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />
      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>{TEXT.title}</h1>
            <h2>{TEXT.lead}</h2>
          </div>
        </div>
      </header>
      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>
              Titus Plattner, Patrick Meier, Simone Luchetta, Sebastian
              Broschinski
            </div>
            <div className='ta updated'>{TEXT.updated}</div>
          </div>
          <div className='ta text' style={{ textAlign: 'left' }}>
            {TEXT.intro}
          </div>
        </div>
      </div>
      <div className='ui vertical segment nopad'>
        <div className='ui  container' style={{ textAlign: 'center' }}>
          {drawprops && switzerland && drawdata.length > 0 && (
            <>
              <DataElementKey
                element={
                  <DataElement
                    notinteraktive
                    name=''
                    code={switzerland.code as string}
                    data={drawprops.map(prop => ({
                      color: colorByValue(
                        switzerland[prop.prop] as number,
                        prop.dir,
                        drawdata.map(m => +m[prop.prop])
                      ),
                      pos: prop.pos,
                      propid: prop.prop,
                      unit: prop.unit,
                      prop: prop.name,
                      relval: +switzerland[prop.prop],
                      val: switzerland[prop.prop + '_abs']
                    }))}
                    setHovered={sethoveredElement}
                  />
                }
                propNames={drawprops.map(m => TEXT.propsinkey[m.prop])}
                locales={[TEXT.better, TEXT.worse, TEXT.ave]}
                data={drawprops.map(prop => ({
                  color: colorByValue(
                    switzerland[prop.prop] as number,
                    prop.dir,
                    drawdata.map(m => +m[prop.prop])
                  ),
                  pos: prop.pos,
                  unit: prop.unit,
                  prop: prop.name,
                  relval: +switzerland[prop.prop],
                  val: switzerland[prop.prop + '_abs']
                }))}
              />
            </>
          )}
        </div>
      </div>
      <div className='ui vertical segment '>
        <div className='ui text container'>
          <div className='ta text'>{TEXT.visual}</div>
        </div>
      </div>
      <div className='ui vertical segment nopad'>
        <div className='ui  container' style={{ textAlign: 'center' }}>
          {drawdata && drawdata.length > 0 && drawprops && (
            <div
              className={'elementcollection ' + (hoveredElement ? 'hover' : '')}
            >
              {drawdata.map(el => {
                return (
                  <div
                    className={
                      'elementcontainer ' +
                      (hoveredElement && hoveredElement.code === el.code
                        ? 'hover'
                        : '')
                    }
                  >
                    <DataElement
                      name={(LOCALE === 'DE' ? el.namede : el.namefr) as string}
                      code={el.code as string}
                      data={drawprops.map(prop => {

                        return ({
                        color: colorByValue(
                          el[prop.prop] as number,
                          prop.dir,
                          drawdata.map(m => +m[prop.prop])
                        ),
                        propid: prop.prop,

                        pos: prop.pos,
                        unit: prop.unit,
                        prop: prop.name,
                        relval: +el[prop.prop],
                        val: +el[prop.prop + '_abs']
                      })})}
                      setHovered={sethoveredElement}
                    />
                  </div>
                )
              })}
              {hoveredElement && (
                <DataElementTooltip
                  pos={hoveredElement.pos}
                  data={hoveredElement.data}
                  setHovered={sethoveredElement}
                />
              )}
            </div>
          )}
        </div>
      </div>
      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta text'>{TEXT.categories}</div>
        </div>

        {drawdata &&
          drawprops &&
          drawprops.map(prop => {
            const median = +drawaverages[prop.prop]
            const newThresh = [0, median, 100]
            radScale.domain(newThresh)
            return (
              <>
                <div className='ui  container' style={{ textAlign: 'center' }}>
                  <PropertyViz
                    prop={prop.prop}
                    name={prop.name}
                    unit={prop.unit}
                    title={prop.title}
                    annotate={prop.anno}
                    dir={prop.dir}
                    minmax={
                      extent(drawdata.map(m => +m[prop.prop + '_abs'])) || [0, 1]
                    }
                    data={drawdata.map(m => ({
                      name: (LOCALE === 'DE' ? m.namede : m.namefr) as string,
                      code: m.code as string,
                      pos: +m[prop.prop],
                      color: colorByValue(
                        m[prop.prop] as number,
                        prop.dir,
                        drawdata.map(m => m[prop.prop]) as number[]
                      )
                    }))}
                  />{' '}
                </div>
                <div
                  className='ui  text container'
                  style={{ textAlign: 'center' }}
                >
                  <div className='ta text' style={{ textAlign: 'left' }}>
                    {TEXT[prop.prop]}
                  </div>
                </div>
              </>
            )
          })}
      </div>
      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta text'>{TEXT.end}</div>
        </div>
      </div>
      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta text'> {TEXT.method}</div>
        </div>
      </div>
      <Footer
        articleId={ARTICLE_ID}
        credits={[
          [TEXT.credits[0], 'Titus Plattner, Patrick Meier, Simone Luchetta'],
          [TEXT.credits[1], 'Patrick Meier, Sebastian Broschinski']
        ]}
      />
    </div>
  )
}
